// Pending "open this site in the extension" handoff from onboarding.
//
// When a user finishes onboarding WITHOUT the extension installed, the chosen
// URL is stashed here so the dashboard can auto-open it the moment the
// extension is detected (see usePendingExtensionOpen). This intentionally uses
// localStorage, not sessionStorage: installing the extension is a multi-step
// detour through the Chrome Web Store that frequently closes the dashboard tab,
// and the resume must survive that. Without it, a user who installs correctly
// but lost the tab gets a project with no path to capture — the activation
// behavior silently zeroes. A TTL guards against a long-abandoned handoff
// resurfacing weeks later. Kept as a separate, side-effect-only-via-storage
// module so it can be unit-tested in isolation (mirrors lib/installLanding.ts).

import { newUuid, type AuthBootstrapResponse } from '@overlayqa/shared';

export interface PendingExtensionOpen {
  url: string;
  urlType: string;
  projectId?: string;
  projectName?: string;
  projectSlug?: string;
  teamId?: string;
  userId?: string;
  source?: string;
  /** Identifies this deliberate open across redirects and extension-worker restarts. */
  handoffId?: string;
  /** epoch ms when written; drives the staleness guard in readPendingExtensionOpen */
  savedAt?: number;
}

const STORAGE_KEY = 'pendingExtensionOpen';

/**
 * Drop a pending handoff older than this. The install-and-return flow takes
 * minutes to a few hours; a week is a generous ceiling before we stop nudging
 * the user to open a site they evidently moved on from.
 */
export const PENDING_OPEN_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export function writePendingExtensionOpen(info: PendingExtensionOpen): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...info, handoffId: info.handoffId ?? newUuid(), savedAt: info.savedAt ?? Date.now() }),
    );
  } catch {
    // ignore quota / private-mode errors — a failed stash just means no auto-open
  }
}

/**
 * Read the pending handoff, or null if absent, corrupt, or stale. `now` is
 * injectable for tests. Purges the entry on any reject path so a bad/stale
 * value can't keep re-triggering the install prompt on every visit.
 */
export function readPendingExtensionOpen(now: number = Date.now()): PendingExtensionOpen | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: PendingExtensionOpen;
  try {
    parsed = JSON.parse(raw) as PendingExtensionOpen;
  } catch {
    clearPendingExtensionOpen();
    return null;
  }

  if (!parsed || typeof parsed.url !== 'string' || parsed.url.length === 0) {
    clearPendingExtensionOpen();
    return null;
  }

  if (typeof parsed.savedAt === 'number' && now - parsed.savedAt > PENDING_OPEN_TTL_MS) {
    clearPendingExtensionOpen();
    return null;
  }

  return parsed;
}

export function clearPendingExtensionOpen(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function pendingOpenWasConfirmed(info: PendingExtensionOpen, receipt: unknown): boolean {
  if (!receipt || typeof receipt !== 'object' || !info.userId || !info.projectId || !info.teamId || !info.savedAt) return false;
  const value = receipt as Record<string, unknown>;
  try {
    const sameOpen = info.handoffId && typeof value.handoffId === 'string'
      ? value.handoffId === info.handoffId
      : value.origin === new URL(info.url).origin;
    return value.userId === info.userId && value.projectId === info.projectId && value.teamId === info.teamId
      && sameOpen && typeof value.openedAt === 'number'
      && value.openedAt >= info.savedAt && value.openedAt <= Date.now();
  } catch { return false; }
}

/** A stored intent never transfers to another account or a team it cannot access. */
export function pendingOpenBelongsToSession(info: PendingExtensionOpen | null, session: AuthBootstrapResponse | undefined): boolean {
  return Boolean(info?.userId && info.teamId && session?.user.id === info.userId
    && (session.activeTeamId === info.teamId || session.teams?.some(team => team.id === info.teamId)));
}
